import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useTeacherLineHistory } from '@/hooks/useTeacherLineHistory';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  teacherCode: string | null;
  teacherName?: string;
}

const fmt = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('th-TH', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export const TeacherLineHistoryDialog: React.FC<Props> = ({ open, onOpenChange, teacherCode, teacherName }) => {
  const { data, isLoading, error } = useTeacherLineHistory(open ? teacherCode : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ประวัติการเชื่อม LINE</DialogTitle>
          <DialogDescription>
            {teacherName ? `${teacherName} — ` : ''}{teacherCode}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive">โหลดประวัติไม่สำเร็จ: {(error as Error).message}</p>
        )}

        {!isLoading && !error && (data?.length ?? 0) === 0 && (
          <p className="text-sm text-muted-foreground py-6 text-center">ยังไม่มีประวัติ</p>
        )}

        {!isLoading && data && data.length > 0 && (
          <div className="space-y-3">
            {data.map((row) => {
              const isFirst = !row.old_user_id;
              return (
                <div key={row.id} className="border rounded-lg p-3 space-y-2 bg-card">
                  <div className="flex items-center justify-between">
                    <Badge variant={isFirst ? 'default' : 'secondary'}>
                      {isFirst ? 'เชื่อมครั้งแรก' : 'เปลี่ยน LINE'}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{fmt(row.created_at)}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">จาก</div>
                      {row.old_user_id ? (
                        <div>
                          <div className="font-medium">{row.old_line_name || '-'}</div>
                          <div className="text-xs text-muted-foreground font-mono break-all">{row.old_user_id}</div>
                        </div>
                      ) : (
                        <div className="text-muted-foreground">(ว่าง)</div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">เป็น</div>
                      {row.new_user_id ? (
                        <div>
                          <div className="font-medium">{row.new_line_name || '-'}</div>
                          <div className="text-xs text-muted-foreground font-mono break-all">{row.new_user_id}</div>
                        </div>
                      ) : (
                        <div className="text-muted-foreground">(ยกเลิกการเชื่อม)</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
